import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { useDebounce } from '../hooks/useDebounce';
import toast from 'react-hot-toast';
import {
  KeyIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  DocumentTextIcon,
  BookOpenIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  BoltIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const TABS = [
  { id: 'overview',       label: 'Overview',       icon: ChartBarIcon },
  { id: 'conversations',  label: 'Conversations',  icon: ChatBubbleLeftRightIcon },
  { id: 'analytics',      label: 'Analytics',      icon: ChartBarIcon },
  { id: 'logs',           label: 'Request Logs',   icon: DocumentTextIcon },
  { id: 'key',            label: 'API Key',        icon: KeyIcon },
  { id: 'docs',           label: 'Docs',           icon: BookOpenIcon },
];

const PHASE_LABELS = {
  initial: 'Initial', phase_1: 'Phase 1', phase_2: 'Phase 2',
  phase_3: 'Phase 3', phase_4: 'Phase 4', phase_5: 'Phase 5',
  phase_6: 'Post-CTA 1', phase_7: 'Post-CTA 2',
  cta: 'CTA', followup: 'Follow-up', completed: 'Completed'
};

const PHASE_ORDER = [
  'initial',
  'phase_1',
  'phase_2',
  'phase_3',
  'phase_4',
  'phase_5',
  'cta',
  'phase_6',
  'phase_7',
  'followup',
  'completed'
];

function phaseSortIndex(phase) {
  const idx = PHASE_ORDER.indexOf(phase);
  return idx === -1 ? PHASE_ORDER.length : idx;
}

const PHASE_COLORS = {
  initial: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  cta: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  followup: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
};

function phaseBadge(phase) {
  const base = 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium';
  const color = PHASE_COLORS[phase] || 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
  return `${base} ${color}`;
}

function StatCard({ label, value, sub, icon: Icon, color = 'blue' }) {
  const colors = {
    blue:   'from-blue-500/10 to-sky-500/10 border-blue-200 dark:border-blue-700',
    green:  'from-green-500/10 to-emerald-500/10 border-green-200 dark:border-green-700',
    purple: 'from-purple-500/10 to-violet-500/10 border-purple-200 dark:border-purple-700',
    orange: 'from-orange-500/10 to-amber-500/10 border-orange-200 dark:border-orange-700',
  };
  const iconColors = {
    blue: 'text-blue-500', green: 'text-green-500', purple: 'text-purple-500', orange: 'text-orange-500'
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-5`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
        {Icon && <Icon className={`h-5 w-5 ${iconColors[color]}`} />}
      </div>
      <div className="text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
      {sub && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

// ─── Tab: Overview ────────────────────────────────────────────────────────────
function OverviewTab({ models }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api-management/overview')
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load overview'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-12 text-gray-500">Loading...</div>;
  if (!data) return null;

  const { overview, modelStats } = data;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Requests"        value={overview.totalRequests.toLocaleString()} sub={`${overview.requestsToday} today`} icon={BoltIcon} color="blue" />
        <StatCard label="Conversations"         value={overview.totalConversations.toLocaleString()} sub={`${overview.conversationsToday} today`} icon={ChatBubbleLeftRightIcon} color="purple" />
        <StatCard label="Conversions"           value={overview.conversions.toLocaleString()} sub={overview.conversionRate + ' rate'} icon={CheckCircleIcon} color="green" />
        <StatCard label="Active Models"         value={modelStats.length} icon={UserGroupIcon} color="orange" />
      </div>

      {modelStats.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Per-Model Breakdown</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Use the Model ID in your API requests as the <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">modelId</code> field</p>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {modelStats.map(m => (
              <div key={m.modelId} className="flex items-center justify-between px-5 py-3 gap-4">
                <div className="min-w-0">
                  <span className="font-medium text-gray-900 dark:text-white text-sm">{m.modelName}</span>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <code className="text-xs font-mono text-gray-500 dark:text-gray-400 truncate">{String(m.modelId)}</code>
                    <button
                      onClick={() => { navigator.clipboard.writeText(String(m.modelId)); toast.success('Model ID copied'); }}
                      className="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors flex-shrink-0"
                      title="Copy Model ID"
                    >
                      <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
                  <span>{m.conversations} convos <span className="text-gray-400">({m.conversationsToday} today)</span></span>
                  <span className="text-green-600 dark:text-green-400">{m.conversions} converted</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Conversations ───────────────────────────────────────────────────────
function ConversationMessageThread({ messages, searchMatch, activeSearch }) {
  const scrollRef = useRef(null);
  const matchIndex = searchMatch?.messageIndex ?? -1;

  useEffect(() => {
    if (matchIndex < 0 || !scrollRef.current) return;
    const target = scrollRef.current.querySelector(`[data-message-index="${matchIndex}"]`);
    if (target) {
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [matchIndex, messages]);

  if (!messages?.length) {
    return <div className="text-xs text-gray-400 italic">No messages stored</div>;
  }

  return (
    <div ref={scrollRef} className="max-h-96 overflow-y-auto space-y-3 pr-2">
      {messages.map((msg, i) => {
        const isMatch = activeSearch && i === matchIndex;
        return (
          <div
            key={msg.messageId || i}
            data-message-index={i}
            className={`flex flex-col gap-0.5 rounded-lg transition-colors ${
              msg.sender === 'bot' ? 'items-end' : 'items-start'
            } ${isMatch ? 'ring-2 ring-amber-400/80 bg-amber-50/60 dark:bg-amber-900/20 p-1' : ''}`}
          >
            <div className="flex items-center gap-1.5 px-1">
              <span className={`text-[10px] font-semibold uppercase tracking-wide ${
                msg.sender === 'bot' ? 'text-blue-400 dark:text-blue-300' : 'text-gray-400 dark:text-gray-500'
              }`}>
                {msg.sender === 'bot' ? 'Bot' : 'User'}
              </span>
              {msg.timestamp && (
                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              {isMatch && (
                <span className="text-[10px] font-medium text-amber-600 dark:text-amber-300">
                  Match
                </span>
              )}
            </div>
            {msg.contentType === 'image' ? (
              <div className={`rounded-lg overflow-hidden border-2 ${
                msg.sender === 'bot'
                  ? 'border-blue-400'
                  : 'border-gray-300 dark:border-gray-600'
              }`}>
                <img
                  src={msg.content}
                  alt="Media"
                  className="h-28 w-28 object-cover cursor-pointer"
                  onClick={() => window.open(msg.content, '_blank')}
                  onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
                <div style={{ display: 'none' }} className="h-28 w-28 items-center justify-center bg-gray-100 dark:bg-gray-800 text-xs text-gray-400">
                  Image
                </div>
              </div>
            ) : (
              <div className={`max-w-xs px-3 py-2 rounded-lg text-xs break-words ${
                msg.sender === 'bot'
                  ? 'bg-blue-500 text-white rounded-br-none'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-bl-none'
              }`}>
                {msg.content}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ConversationsTab({ models }) {
  const [conversations, setConversations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [modelFilter, setModelFilter] = useState('');
  const [phaseFilter, setPhaseFilter] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [cleanModal, setCleanModal] = useState(false);
  const [cleanScope, setCleanScope] = useState('api');
  const [cleanModelId, setCleanModelId] = useState('');
  const [cleaning, setCleaning] = useState(false);
  const [page, setPage] = useState(0);
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit, offset: page * limit });
      if (modelFilter) params.append('modelId', modelFilter);
      if (phaseFilter) params.append('phase', phaseFilter);
      if (search) params.append('search', debouncedSearch);
      const r = await api.get(`/api-management/conversations?${params}`);
      setConversations(r.data.conversations || []);
      setTotal(r.data.total || 0);
    } catch { toast.error('Failed to load conversations'); }
    finally { setLoading(false); }
  }, [modelFilter, phaseFilter, debouncedSearch, page]);

  useEffect(() => { load(); }, [load]);

  async function handleClean() {
    if (!window.confirm(`Delete conversations? This cannot be undone.`)) return;
    setCleaning(true);
    try {
      const params = new URLSearchParams({ scope: cleanScope });
      if (cleanModelId) params.append('modelId', cleanModelId);
      if (cleanScope === 'all') params.append('confirm', 'yes');
      const r = await api.delete(`/api-management/conversations?${params}`);
      toast.success(r.data.message || 'Conversations deleted');
      setCleanModal(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to delete');
    } finally { setCleaning(false); }
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <input
          type="text"
          placeholder="Search identifier, username, or message text..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          className="flex-1 min-w-48 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
        />
        <select
          value={modelFilter}
          onChange={e => { setModelFilter(e.target.value); setPage(0); }}
          className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
        >
          <option value="">All models</option>
          {models.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
        </select>
        <select
          value={phaseFilter}
          onChange={e => { setPhaseFilter(e.target.value); setPage(0); }}
          className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none"
        >
          <option value="">All phases</option>
          {Object.entries(PHASE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button
          onClick={() => setCleanModal(true)}
          className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        >
          <TrashIcon className="h-4 w-4" /> Clean Conversations
        </button>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            No API conversations yet. Start making requests to <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">/api/v1/generateResponse</code>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                <th className="text-left px-4 py-3">User Identifier</th>
                <th className="text-left px-4 py-3">Model</th>
                <th className="text-left px-4 py-3">Phase</th>
                <th className="text-left px-4 py-3">Exchanges</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Last Activity</th>
                <th className="text-left px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {conversations.map(conv => (
                <React.Fragment key={conv._id}>
                  <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-gray-700 dark:text-gray-300">
                      <div>{conv.apiUserIdentifier || conv.xUserId}</div>
                      {debouncedSearch && conv.searchMatch?.snippet && (
                        <div className="mt-1 max-w-xs text-[11px] font-sans text-amber-700 dark:text-amber-300 line-clamp-2">
                          <span className="font-medium">
                            {conv.searchMatch.sender === 'bot' ? 'Bot' : 'User'}:
                          </span>{' '}
                          {conv.searchMatch.snippet}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-900 dark:text-white">
                      {conv.modelId?.name || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={phaseBadge(conv.conversationPhase)}>
                        {PHASE_LABELS[conv.conversationPhase] || conv.conversationPhase}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{conv.totalExchanges}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        {conv.conversionTracking?.status === 'confirmed' ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400 font-medium">
                            <CheckCircleIcon className="h-3.5 w-3.5" /> Converted
                          </span>
                        ) : conv.conversationPhase === 'completed' ? (
                          <span className="text-xs text-gray-400">Completed</span>
                        ) : (
                          <span className="text-xs text-blue-500 font-medium">Active</span>
                        )}
                        {conv.ctaPostSharing?.linkShared && (
                          <span className="inline-flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 font-medium">
                            <CheckCircleIcon className="h-3 w-3" /> CTA Sent
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                      {conv.lastActivity ? new Date(conv.lastActivity).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setExpanded(expanded === conv._id ? null : conv._id)}
                        className="text-blue-500 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                      >
                        {expanded === conv._id ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                      </button>
                    </td>
                  </tr>
                  {expanded === conv._id && (
                    <tr>
                      <td colSpan={7} className="px-4 pb-4 bg-gray-50 dark:bg-gray-900/50">
                        {/* CTA link summary bar */}
                        {conv.ctaPostSharing?.linkShared && (
                          <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700">
                            <CheckCircleIcon className="h-3.5 w-3.5 text-purple-500 shrink-0" />
                            <span className="text-xs text-purple-700 dark:text-purple-300 font-medium">CTA link shared</span>
                            {conv.ctaLinkUsed?.url && (
                              <a
                                href={conv.ctaLinkUsed.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-purple-600 dark:text-purple-400 underline truncate max-w-xs"
                              >
                                {conv.ctaLinkUsed.url}
                              </a>
                            )}
                            {conv.ctaPostSharing?.sharedAt && (
                              <span className="text-xs text-purple-400 ml-auto shrink-0">
                                {new Date(conv.ctaPostSharing.sharedAt).toLocaleString()}
                              </span>
                            )}
                          </div>
                        )}
                        {/* Message count */}
                        {(conv.messages || []).length > 0 && (
                          <div className="mt-2 mb-1 text-[10px] text-gray-400 dark:text-gray-500 px-1">
                            {conv.messages.length} message{conv.messages.length !== 1 ? 's' : ''} total
                          </div>
                        )}
                        <ConversationMessageThread
                          messages={conv.messages}
                          searchMatch={conv.searchMatch}
                          activeSearch={debouncedSearch}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>{total} total conversations</span>
          <div className="flex gap-2">
            <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
              className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              Previous
            </button>
            <span className="px-3 py-1">{page + 1} / {Math.ceil(total / limit)}</span>
            <button disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)}
              className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              Next
            </button>
          </div>
        </div>
      )}

      {/* Clean modal */}
      {cleanModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <TrashIcon className="h-6 w-6 text-red-500" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Clean Conversations</h2>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Permanently delete conversations from MongoDB. This cannot be undone.
            </p>
            <div className="space-y-3 mb-6">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Scope</label>
                <select
                  value={cleanScope}
                  onChange={e => setCleanScope(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="api">All API conversations</option>
                  <option value="model">API conversations for a specific model</option>
                  <option value="all">ALL conversations (API + Automation)</option>
                </select>
              </div>
              {(cleanScope === 'model') && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Model</label>
                  <select
                    value={cleanModelId}
                    onChange={e => setCleanModelId(e.target.value)}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select model...</option>
                    {models.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
                  </select>
                </div>
              )}
              {cleanScope === 'all' && (
                <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <ExclamationCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700 dark:text-red-400">
                    This will delete <strong>ALL</strong> conversations including automation conversations. This is irreversible.
                  </p>
                </div>
              )}
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setCleanModal(false)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                Cancel
              </button>
              <button onClick={handleClean} disabled={cleaning}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors">
                {cleaning ? 'Deleting...' : 'Delete Conversations'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Analytics ───────────────────────────────────────────────────────────
function AnalyticsTab({ models }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7days');
  const [modelFilter, setModelFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ period });
    if (modelFilter) params.append('modelId', modelFilter);
    api.get(`/api-management/analytics?${params}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [period, modelFilter]);

  if (loading) return <div className="text-center py-12 text-gray-500">Loading...</div>;
  if (!data) return null;

  const { summary, daily, phaseDistribution } = data;
  const maxConversations = Math.max(...daily.map(d => d.conversations), 1);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex gap-3 flex-wrap">
        {['today', '3days', '7days', '30days', 'all'].map(p => (
          <button key={p} onClick={() => setPeriod(p)}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              period === p
                ? 'bg-blue-600 text-white'
                : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}>
            {p === 'today' ? 'Today' : p === 'all' ? 'All Time' : p}
          </button>
        ))}
        <select
          value={modelFilter}
          onChange={e => setModelFilter(e.target.value)}
          className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="">All models</option>
          {models.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
        </select>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Conversations"  value={summary.totalConversations} icon={ChatBubbleLeftRightIcon} color="blue" />
        <StatCard
          label="Idle"
          value={summary.idleConversations ?? 0}
          sub="Bot awaiting user reply"
          icon={ExclamationCircleIcon}
          color="orange"
        />
        <StatCard label="API Requests"   value={summary.totalRequests} icon={BoltIcon} color="purple" />
        <StatCard label="Conversions"    value={summary.conversions} sub={`${summary.totalConversations > 0 ? ((summary.conversions / summary.totalConversations) * 100).toFixed(1) : 0}% rate`} icon={CheckCircleIcon} color="green" />
        <StatCard label="Avg Response"   value={`${summary.avgResponseMs}ms`} icon={ClockIcon} color="orange" />
      </div>

      {/* Follow-up response rates */}
      {summary.followUpAnalytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Follow-ups · Before CTA</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {summary.followUpAnalytics.beforeCta.responseRate}%
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {summary.followUpAnalytics.beforeCta.responses} replies / {summary.followUpAnalytics.beforeCta.sent} sent
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Follow-ups · After CTA</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {summary.followUpAnalytics.afterCta.responseRate}%
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {summary.followUpAnalytics.afterCta.responses} replies / {summary.followUpAnalytics.afterCta.sent} sent
            </p>
          </div>
        </div>
      )}

      {/* Daily chart */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Daily Conversations</h3>
        <div className="flex items-end gap-1 h-32">
          {daily.map(d => (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group relative">
              <div className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                {d.date}: {d.conversations} convos, {d.conversions} conversions
              </div>
              <div
                className="w-full bg-blue-500 dark:bg-blue-600 rounded-sm opacity-80 hover:opacity-100 transition-opacity cursor-default"
                style={{ height: `${Math.max(2, (d.conversations / maxConversations) * 100)}%` }}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-400">
          <span>{daily[0]?.date}</span>
          <span>{daily[daily.length - 1]?.date}</span>
        </div>
      </div>

      {/* Phase distribution */}
      {phaseDistribution.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Phase Distribution</h3>
          <div className="space-y-2">
            {[...phaseDistribution]
              .sort((a, b) => phaseSortIndex(a.phase) - phaseSortIndex(b.phase))
              .map(({ phase, count }) => {
              const total = phaseDistribution.reduce((s, p) => s + p.count, 0);
              const pct = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
              return (
                <div key={phase} className="flex items-center gap-3">
                  <span className="w-24 text-xs text-gray-600 dark:text-gray-400 text-right shrink-0">
                    {PHASE_LABELS[phase] || phase}
                  </span>
                  <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs text-gray-500 w-16 shrink-0">{count} ({pct}%)</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Logs ────────────────────────────────────────────────────────────────
function aiRoutingLabel(routing) {
  if (!routing) return null;
  if (routing.label) return routing.label;
  if (routing.provider === 'template') return 'Template (local CTA/scripts)';
  const names = { grok: 'Grok', deepseek: 'DeepSeek', template: 'Template' };
  const name = names[routing.provider] || routing.provider || '—';
  return routing.model ? `${name} · ${routing.model}` : name;
}

function aiRoutingBadgeClass(provider) {
  switch (provider) {
    case 'deepseek': return 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300';
    case 'grok': return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300';
    case 'template': return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
    default: return 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400';
  }
}

function LogsTab({ models }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modelFilter, setModelFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [userIdInput, setUserIdInput] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [page, setPage] = useState(0);
  const [expandedLog, setExpandedLog] = useState(null);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit, offset: page * limit });
      if (modelFilter) params.append('modelId', modelFilter);
      if (userIdFilter) params.append('userIdentifier', userIdFilter);
      const r = await api.get(`/api-management/logs?${params}`);
      setLogs(r.data.logs || []);
      setTotal(r.data.total || 0);
    } catch { toast.error('Failed to load logs'); }
    finally { setLoading(false); }
  }, [modelFilter, userIdFilter, page]);

  useEffect(() => { load(); }, [load]);

  function statusBadge(code) {
    if (code >= 200 && code < 300) return 'text-green-600 dark:text-green-400';
    if (code >= 400) return 'text-red-600 dark:text-red-400';
    return 'text-yellow-600 dark:text-yellow-400';
  }

  function copyUserId(id, e) {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    toast.success('User ID copied');
    setTimeout(() => setCopiedId(null), 2000);
  }

  function filterByUserId(id, e) {
    e.stopPropagation();
    setUserIdInput(id);
    setUserIdFilter(id);
    setPage(0);
  }

  function applyUserIdFilter(e) {
    e.preventDefault();
    setUserIdFilter(userIdInput.trim());
    setPage(0);
  }

  function clearUserIdFilter() {
    setUserIdInput('');
    setUserIdFilter('');
    setPage(0);
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center flex-wrap">
        <select
          value={modelFilter}
          onChange={e => { setModelFilter(e.target.value); setPage(0); }}
          className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="">All models</option>
          {models.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
        </select>
        {/* User ID filter */}
        <form onSubmit={applyUserIdFilter} className="flex items-center gap-1">
          <input
            type="text"
            placeholder="Filter by user ID..."
            value={userIdInput}
            onChange={e => setUserIdInput(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none w-52"
          />
          <button type="submit" className="px-3 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors">
            Filter
          </button>
          {userIdFilter && (
            <button type="button" onClick={clearUserIdFilter}
              className="px-2 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Clear filter"
            >
              ✕
            </button>
          )}
        </form>
        <button onClick={load} className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" title="Refresh">
          <ArrowPathIcon className="h-4 w-4" />
        </button>
        <span className="text-sm text-gray-500">
          {userIdFilter
            ? <><span className="font-mono text-blue-500 dark:text-blue-400">{userIdFilter}</span> — {total.toLocaleString()} requests</>
            : <>{total.toLocaleString()} requests logged</>
          }
        </span>
      </div>

      {/* Active filter pill */}
      {userIdFilter && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">Filtering by user:</span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-mono bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700">
            {userIdFilter}
            <button onClick={clearUserIdFilter} className="text-blue-400 hover:text-blue-600 dark:hover:text-blue-200 ml-0.5" title="Clear filter">✕</button>
          </span>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            {userIdFilter ? `No logs found for user ID "${userIdFilter}"` : 'No requests logged yet'}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-700 text-gray-400 uppercase tracking-wide">
                <th className="text-left px-4 py-3">Time</th>
                <th className="text-left px-4 py-3">Model</th>
                <th className="text-left px-4 py-3">User ID</th>
                <th className="text-left px-4 py-3">Message Sent</th>
                <th className="text-left px-4 py-3">AI Destination</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Duration</th>
                <th className="text-left px-4 py-3">Flags</th>
                <th className="text-left px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50 font-mono">
              {logs.map(log => (
                <React.Fragment key={log._id}>
                  <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer" onClick={() => setExpandedLog(expandedLog === log._id ? null : log._id)}>
                    <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400">{log.modelId?.name || '—'}</td>
                    <td className="px-4 py-2.5 max-w-36">
                      {log.userIdentifier ? (
                        <div className="flex items-center gap-1 group">
                          <span className="text-gray-600 dark:text-gray-300 truncate max-w-24" title={log.userIdentifier}>
                            {log.userIdentifier}
                          </span>
                          {/* Copy button — always visible, turns green on copy */}
                          <button
                            onClick={e => copyUserId(log.userIdentifier, e)}
                            title="Copy user ID"
                            className={`flex-shrink-0 p-0.5 rounded transition-colors ${
                              copiedId === log.userIdentifier
                                ? 'text-green-500'
                                : 'text-gray-300 dark:text-gray-600 hover:text-blue-500 dark:hover:text-blue-400'
                            }`}
                          >
                            <ClipboardDocumentIcon className="h-3 w-3" />
                          </button>
                          {/* Filter by this user button */}
                          <button
                            onClick={e => filterByUserId(log.userIdentifier, e)}
                            title="Filter logs by this user"
                            className="flex-shrink-0 p-0.5 rounded text-gray-300 dark:text-gray-600 hover:text-purple-500 dark:hover:text-purple-400 transition-colors"
                          >
                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                            </svg>
                          </button>
                        </div>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300 max-w-48 truncate">
                      {(() => {
                        // New logs have requestData (JSON), old logs have requestMessage
                        if (log.requestData) {
                          try {
                            const d = JSON.parse(log.requestData);
                            if (d.type === 'opener') return <span className="text-blue-400 italic">opener</span>;
                            if (d.type === 'follow-up') return <span className="text-purple-400 italic">follow-up</span>;
                            return <span title={d.message}>{d.message || '—'}</span>;
                          } catch { return <span className="text-gray-400">—</span>; }
                        }
                        // Legacy: requestMessage may contain bad data — skip it
                        return <span className="text-gray-400 italic">legacy log</span>;
                      })()}
                    </td>
                    <td className="px-4 py-2.5 max-w-44">
                      {(() => {
                        const routing = log.aiRouting || (() => {
                          if (!log.requestData) return null;
                          try { return JSON.parse(log.requestData).aiRouting || null; }
                          catch { return null; }
                        })();
                        const label = aiRoutingLabel(routing);
                        if (!label) return <span className="text-gray-400">—</span>;
                        return (
                          <span
                            className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-sans truncate max-w-full ${aiRoutingBadgeClass(routing.provider)}`}
                            title={[
                              routing.baseUrl && `Chat: ${routing.baseUrl}`,
                              routing.visionBaseUrl && `Vision: ${routing.visionBaseUrl}`,
                              routing.visionModel && `Vision model: ${routing.visionModel}`
                            ].filter(Boolean).join('\n') || label}
                          >
                            {label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className={`px-4 py-2.5 font-semibold ${statusBadge(log.statusCode)}`}>{log.statusCode}</td>
                    <td className="px-4 py-2.5 text-gray-500">{log.requestDurationMs}ms</td>
                    <td className="px-4 py-2.5">
                      <div className="flex gap-1 flex-wrap">
                        {log.responseFlags?.converted && <span className="px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs">converted</span>}
                        {log.responseFlags?.aiCreditOver && <span className="px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs">credit_over</span>}
                        {log.responseFlags?.ignored && <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs">ignored</span>}
                        {log.statusCode >= 400 && <span className="px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs" title={log.error}>error</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      {expandedLog === log._id
                        ? <EyeSlashIcon className="h-3.5 w-3.5 text-gray-400" />
                        : <EyeIcon className="h-3.5 w-3.5 text-gray-400" />}
                    </td>
                  </tr>
                  {expandedLog === log._id && (
                    <tr>
                      <td colSpan={9} className="px-4 pb-4 bg-gray-50 dark:bg-gray-900/50">
                        <div className="mt-2 space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {/* Request */}
                            <div>
                              <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-400 mb-1">Request</div>
                              <pre className="bg-gray-900 text-green-300 rounded-lg px-3 py-2 text-[11px] overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap break-words">
                                {log.requestData ? (() => {
                                  try { return JSON.stringify(JSON.parse(log.requestData), null, 2); }
                                  catch { return log.requestData; }
                                })() : log.requestMessage
                                  ? `{ "message": ${JSON.stringify(log.requestMessage)} }`
                                  : '—'}
                              </pre>
                            </div>
                            {/* Response */}
                            <div>
                              <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-400 mb-1">Response</div>
                              <pre className="bg-gray-900 text-blue-300 rounded-lg px-3 py-2 text-[11px] overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap break-words">
                                {log.responseContent ? (() => {
                                  try { return JSON.stringify(JSON.parse(log.responseContent), null, 2); }
                                  catch { return log.responseContent; }
                                })() : '—'}
                              </pre>
                            </div>
                          </div>
                          {/* Meta row */}
                          <div className="flex flex-wrap gap-4 text-[10px] text-gray-400 pt-1">
                            <span>Endpoint: <span className="text-gray-500 dark:text-gray-300 font-mono">{log.endpoint}</span></span>
                            {(() => {
                              const routing = log.aiRouting || (() => {
                                if (!log.requestData) return null;
                                try { return JSON.parse(log.requestData).aiRouting || null; }
                                catch { return null; }
                              })();
                              if (!routing) return null;
                              return (
                                <>
                                  <span>
                                    Chat API:{' '}
                                    <span className="text-gray-500 dark:text-gray-300 font-mono">
                                      {routing.baseUrl || 'local'}
                                      {routing.model ? ` · ${routing.model}` : ''}
                                    </span>
                                  </span>
                                  {routing.visionProvider && (
                                    <span>
                                      Vision API:{' '}
                                      <span className="text-gray-500 dark:text-gray-300 font-mono">
                                        {routing.visionBaseUrl || '—'}
                                        {routing.visionModel ? ` · ${routing.visionModel}` : ''}
                                      </span>
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                            <span>Duration: <span className="text-gray-500 dark:text-gray-300">{log.requestDurationMs}ms</span></span>
                            <span>Content: <span className="text-gray-500 dark:text-gray-300">{(log.contentTypes || []).join(', ') || '—'}</span></span>
                            {log.statusCode >= 400 && log.error && (
                              <span className="text-red-400">Error: {log.error}</span>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > limit && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>{total.toLocaleString()} total</span>
          <div className="flex gap-2">
            <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
              className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40">Previous</button>
            <span className="px-3 py-1">{page + 1} / {Math.ceil(total / limit)}</span>
            <button disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)}
              className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Models reference panel (used inside API Key tab) ────────────────────────
function ApiKeyModels() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api-management/models')
      .then(r => setModels(r.data.models || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (models.length === 0) return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Your Models</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400">No models found. Create a model first in the <strong>Models</strong> section.</p>
    </div>
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="font-semibold text-gray-900 dark:text-white">Your Models</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          Copy a Model ID and use it as the <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">modelId</code> field in every API request
        </p>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {models.map(m => (
          <div key={m._id} className="flex items-center justify-between px-5 py-3 gap-4">
            <div className="min-w-0">
              <div className="font-medium text-gray-900 dark:text-white text-sm">{m.name}</div>
              <div className="text-xs text-gray-400 dark:text-gray-500">Age {m.age} · {m.city}</div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <code className="text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded">
                {String(m._id)}
              </code>
              <button
                onClick={() => { navigator.clipboard.writeText(String(m._id)); toast.success(`"${m.name}" model ID copied`); }}
                className="p-1.5 text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                title="Copy Model ID"
              >
                <ClipboardDocumentIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Tab: API Key ──────────────────────────────────────────────────────────────
function ApiKeyTab() {
  const [keyInfo, setKeyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showKey, setShowKey] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [toggling, setToggling] = useState(false);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    api.get('/api-management/key')
      .then(r => setKeyInfo(r.data))
      .catch(() => toast.error('Failed to load API key'))
      .finally(() => setLoading(false));
  }, []);

  async function regenerate() {
    if (!window.confirm('Regenerate API key? The old key will stop working immediately.')) return;
    setRegenerating(true);
    try {
      const r = await api.post('/api-management/key/regenerate');
      setKeyInfo(k => ({ ...k, apiKey: r.data.apiKey }));
      toast.success('API key regenerated');
    } catch { toast.error('Failed to regenerate key'); }
    finally { setRegenerating(false); }
  }

  async function toggle() {
    setToggling(true);
    try {
      const r = await api.patch('/api-management/key/toggle');
      setKeyInfo(k => ({ ...k, apiEnabled: r.data.apiEnabled }));
      toast.success(`API access ${r.data.apiEnabled ? 'enabled' : 'disabled'}`);
    } catch { toast.error('Failed to toggle API access'); }
    finally { setToggling(false); }
  }

  function copyKey() {
    navigator.clipboard.writeText(keyInfo.apiKey);
    toast.success('API key copied to clipboard');
  }

  if (loading) return <div className="text-center py-12 text-gray-500">Loading...</div>;
  if (!keyInfo) return null;

  const maskedKey = keyInfo.apiKey
    ? keyInfo.apiKey.slice(0, 8) + '••••••••••••••••••••••••••••••••' + keyInfo.apiKey.slice(-4)
    : '';

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Key display */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <KeyIcon className="h-5 w-5 text-blue-500" />
            <h3 className="font-semibold text-gray-900 dark:text-white">Your API Key</h3>
          </div>
          <span className={`px-2 py-1 text-xs rounded-full font-medium ${
            keyInfo.apiEnabled
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
          }`}>
            {keyInfo.apiEnabled ? 'Active' : 'Disabled'}
          </span>
        </div>

        <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <code className="flex-1 text-sm font-mono text-gray-800 dark:text-gray-200 break-all">
            {showKey ? keyInfo.apiKey : maskedKey}
          </code>
          <button onClick={() => setShowKey(s => !s)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 flex-shrink-0">
            {showKey ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
          </button>
          <button onClick={copyKey} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 flex-shrink-0">
            <ClipboardDocumentIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={regenerate}
            disabled={regenerating}
            className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            <ArrowPathIcon className={`h-4 w-4 ${regenerating ? 'animate-spin' : ''}`} />
            Regenerate
          </button>
          <button
            onClick={toggle}
            disabled={toggling}
            className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg transition-colors disabled:opacity-50 ${
              keyInfo.apiEnabled
                ? 'border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                : 'border border-green-300 dark:border-green-700 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
            }`}
          >
            {keyInfo.apiEnabled ? 'Disable API Access' : 'Enable API Access'}
          </button>
        </div>
      </div>

      {/* Models reference */}
      <ApiKeyModels />

      {/* Usage */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Quick Start</h3>
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">Include your API key as <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">licensekey</code> in every request body, or as the <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">x-api-key</code> header.</p>
          <pre className={`text-xs p-4 rounded-lg overflow-x-auto ${isDarkMode ? 'bg-gray-900 text-gray-200' : 'bg-gray-900 text-gray-100'}`}>
{`POST https://wingr.io/api/v1/generateResponse
Content-Type: application/json

{
  "licensekey": "${showKey ? keyInfo.apiKey : maskedKey}",
  "modelId": "<your-model-id>",
  "userInfos": {
    "useridentifier": "unique-user-123"
  },
  "chatHistory": [
    {
      "role": "user",
      "content": "hey what's up",
      "timestamp": ${Math.floor(Date.now() / 1000)}
    }
  ]
}`}
          </pre>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Docs ────────────────────────────────────────────────────────────────
function DocsTab() {
  const { isDarkMode } = useTheme();
  const pre = `${isDarkMode ? 'bg-gray-900 text-gray-200' : 'bg-gray-900 text-gray-100'} text-xs p-4 rounded-lg overflow-x-auto`;

  return (
    <div className="space-y-8 max-w-3xl text-sm text-gray-700 dark:text-gray-300">
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">API Reference</h2>
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          <span className="text-gray-500 dark:text-gray-400 text-sm">Base URL:</span>
          <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono text-blue-600 dark:text-blue-400">https://wingr.io/api/v1</code>
        </div>
      </div>

      {/* Auth */}
      <section>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">Authentication</h3>
        <p className="mb-3">Pass your API key (from the <strong>API Key</strong> tab) as <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">licensekey</code> in every request body, or as the <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">x-api-key</code> HTTP header.</p>
      </section>

      {/* generateResponse */}
      <section>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          <span className="text-blue-500 dark:text-blue-400 mr-2">POST</span>
          https://wingr.io/api/v1/generateResponse
        </h3>
        <p className="mb-3">Generate an AI chatbot response for a given user. Conversations are persisted automatically — state (phase, exchanges, CTA) is maintained across calls using the <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">useridentifier</code>.</p>

        <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-1">Required fields</h4>
        <pre className={pre}>{`POST https://wingr.io/api/v1/generateResponse
Content-Type: application/json

{
  "licensekey":  "wgr_...",         // Your API key
  "modelId":     "64abc123...",      // Model ObjectId (from /getModelsAndPresets)
  "userInfos": {
    "useridentifier": "user-123"     // Unique ID for the person being chatted with
  },
  "chatHistory": [                   // Previous messages (empty [] for opener)
    { "role": "user",    "content": "hey", "timestamp": 1730755200 },
    { "role": "bot",     "content": "hi!", "timestamp": 1730755210 }
  ]
}`}</pre>

        <h4 className="font-medium text-gray-800 dark:text-gray-200 mt-4 mb-1">Optional fields</h4>
        <pre className={pre}>{`{
  "timezone":      "America/New_York",
  "city":          "New York",         // Override model's city
  "interestLevel": "Medium",           // Low | Medium | High
  "language":      "English",
  "phaseGoal":     "Build rapport",
  "userInfos": {
    "name":     "Jake",
    "username": "jake_cool",
    "age":      25,
    "location": "New York"
  }
}`}</pre>

        <h4 className="font-medium text-gray-800 dark:text-gray-200 mt-4 mb-1">Response</h4>
        <pre className={pre}>{`{
  "content": [
    { "type": "text",  "content": "hey!",            "delayBeforeMs": 0 },
    { "type": "text",  "content": "how are you",     "delayBeforeMs": 1820 },
    { "type": "image", "content": "https://...",     "delayBeforeMs": 1240, "mediaPool": "Primary" }
  ],
  "converted":  false,
  "underage":   false,
  "statistics": {
    "totalExchanges":     3,
    "currentPhase":       "phase_1",
    "currentPhaseNumber": 1,
    "ctaShared":          false,
    "converted":          false
  }
}`}</pre>

        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          <strong>delayBeforeMs</strong> — natural typing-cadence delay (in milliseconds) to wait
          BEFORE sending each item. The first item is always <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">0</code>;
          subsequent items are sized to their own word count (~1-2s for short bubbles, ~4-6s for longer ones,
          +0.5s for images). Use it to replay the burst with realistic cadence so it does not look like a bot
          firing everything at once.
        </p>

        <h4 className="font-medium text-gray-800 dark:text-gray-200 mt-4 mb-1">Automatic triggers</h4>
        <ul className="list-disc pl-5 space-y-1 text-gray-600 dark:text-gray-400">
          <li><strong>Opener:</strong> <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">chatHistory: []</code> — generates an opening message</li>
          <li><strong>Follow-up:</strong> last message has <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">role: &quot;bot&quot;</code> — idle re-engagement (enable per model → Follow Up tab). Returns <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">followUpNotDue: true</code> until idle threshold (15–60 min) is met.</li>
          <li><strong>Response:</strong> last message has <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">role: &quot;user&quot;</code> — generates an AI reply</li>
        </ul>
      </section>

      {/* getDueFollowUps */}
      <section>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          <span className="text-blue-500 dark:text-blue-400 mr-2">POST</span>
          https://wingr.io/api/v1/getDueFollowUps
        </h3>
        <p className="mb-3">Poll every minute for API conversations that are idle and ready for an auto follow-up. For each entry, call <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">generateResponse</code> with bot-last <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">chatHistory</code>.</p>
        <pre className={pre}>{`POST https://wingr.io/api/v1/getDueFollowUps
Content-Type: application/json

{
  "licensekey": "wgr_...",
  "modelId": "64abc...",   // optional filter
  "limit": 100
}

// Response
{
  "success": true,
  "count": 2,
  "due": [
    {
      "userIdentifier": "user-123",
      "modelId": "64abc...",
      "followUpType": "beforeCta",
      "idleMinutes": 30,
      "sentInStreak": 0,
      "maxMessages": 3
    }
  ]
}`}</pre>
      </section>

      {/* getModelsAndPresets */}
      <section>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          <span className="text-blue-500 dark:text-blue-400 mr-2">POST</span>
          https://wingr.io/api/v1/getModelsAndPresets
        </h3>
        <p className="mb-3">List all models available for your API key.</p>
        <pre className={pre}>{`POST https://wingr.io/api/v1/getModelsAndPresets
Content-Type: application/json

// Request
{ "licensekey": "wgr_..." }

// Response
{
  "success": true,
  "data": {
    "models": [
      { "modelId": "64abc...", "name": "Lily", "age": 22, "city": "New York" }
    ]
  }
}`}</pre>
      </section>

      {/* statistics */}
      <section>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
          <span className="text-blue-500 dark:text-blue-400 mr-2">POST</span>
          https://wingr.io/api/v1/statistics
        </h3>
        <p className="mb-3">Get conversation statistics for your API conversations.</p>
        <pre className={pre}>{`POST https://wingr.io/api/v1/statistics
Content-Type: application/json

// Request
{
  "licensekey": "wgr_...",
  "modelId": "64abc...",   // optional
  "period": "7days"        // today | 3days | 7days | 30days | all
}

// Response
{
  "success": true,
  "statistics": {
    "totalRequests":      1250,
    "totalConversations": 156,
    "totalConversions":   23,
    "ctaShared":          45,
    "avgResponseMs":      420,
    "conversionRate":     "14.7%"
  }
}`}</pre>
      </section>

      {/* Error codes */}
      <section>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">Error Codes</h3>
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {[
            ['400', 'Validation failed — missing or invalid field'],
            ['401', 'Invalid or missing API key (licensekey)'],
            ['403', 'Model not found or API access disabled'],
            ['429', 'Rate limit exceeded'],
            ['500', 'Internal server error'],
          ].map(([code, desc]) => (
            <div key={code} className="flex items-center gap-4 px-4 py-3 border-b last:border-0 border-gray-100 dark:border-gray-700">
              <code className={`text-sm font-mono font-bold ${
                code.startsWith('2') ? 'text-green-600 dark:text-green-400' :
                code.startsWith('4') ? 'text-red-600 dark:text-red-400' :
                'text-orange-600 dark:text-orange-400'
              }`}>{code}</code>
              <span className="text-gray-600 dark:text-gray-400">{desc}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function ApiManagement() {
  const [activeTab, setActiveTab] = useState('overview');
  const [models, setModels] = useState([]);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    api.get('/api-management/models')
      .then(r => setModels(r.data.models || []))
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">API</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Connect third-party platforms to your AI chatbot via REST API
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-1 overflow-x-auto">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'overview'      && <OverviewTab models={models} />}
        {activeTab === 'conversations' && <ConversationsTab models={models} />}
        {activeTab === 'analytics'     && <AnalyticsTab models={models} />}
        {activeTab === 'logs'          && <LogsTab models={models} />}
        {activeTab === 'key'           && <ApiKeyTab />}
        {activeTab === 'docs'          && <DocsTab />}
      </div>
    </div>
  );
}
